import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { VerticalViewer } from "../components/feature/viewer/VerticalViewer.js";
import { HorizontalViewer } from "../components/feature/viewer/HorizontalViewer.js";
import { ViewerLayout } from "../components/feature/viewer/ViewerLayout.js";
import { ViewerContent } from "../components/feature/viewer/ViewerContent.js";
// Common Modules
import { ViewerHeader } from "../components/feature/viewer/ViewerHeader.js";
import { ViewerFooter } from "../components/feature/viewer/ViewerFooter.js";
import { ViewerController } from "../components/feature/viewer/ViewerController.js";
import { ViewerResumeEngine } from "../components/feature/viewer/ViewerResumeEngine.js";
import { CopyrightWarning } from "../components/feature/viewer/CopyrightWarning.js";
import { EpisodeAdapter } from "../services/episode-adapter.service.js";
import { ContentService } from "../services/content.service.js";
import { PointService } from "../services/point.service.js";
import { PurchaseModal } from "../components/modals/PurchaseModal.js";
// Feature Modules
import { CommentSection } from "../components/feature/comment/CommentSection.js";

const db = getFirestore(app);

export const WebtoonViewerPage = async (seriesId, episodeId, options = {}) => {
    const { isEmbedded = false, previewData = null } = options;

    // 1. Security Check
    if (!isEmbedded && !previewData && !window.user) {
        window.Modal.toast("로그인이 필요한 서비스입니다.");
        window.router.navigate('/login');
        return document.createElement('div');
    }

    window.scrollTo(0, 0);

    // 2. Data Fetching
    let epData = null;
    let seriesData = null;
    let hasNext = false;
    let hasPrev = false;
    let nextIndex = -1;
    let prevIndex = -1;
    let allEpisodes = [];
    const epIndex = parseInt(episodeId) || 0;

    if (previewData) {
        seriesData = { title: previewData.seriesTitle || "Preview Series" };
        epData = previewData;
    } else {
        try {
            const seriesDoc = await getDoc(doc(db, "series", seriesId));
            if (seriesDoc.exists()) {
                seriesData = seriesDoc.data();

                // [REFACTOR] Standardized Episode Logic via Adapter
                const subEpisodes = await ContentService.getEpisodes(seriesId);
                allEpisodes = EpisodeAdapter.adapt(seriesData, subEpisodes);

                // Robust Episode Lookup (ID or Index)
                const targetId = String(episodeId);
                const currentEp = allEpisodes.find(ep => String(ep.originalId) === targetId || String(ep.index - 1) === targetId)
                    || allEpisodes[parseInt(episodeId) || 0];

                if (currentEp) {
                    epData = currentEp;

                    // Security: Filter Drafts
                    const isAuthor = window.user && window.user.uid === seriesData.authorId;
                    const readableEpisodes = EpisodeAdapter.filterForReader(allEpisodes, isAuthor);

                    // Navigation Calculation
                    const currentIndexInList = readableEpisodes.findIndex(ep => ep.index === currentEp.index);
                    const prevEp = readableEpisodes[currentIndexInList - 1];
                    const nextEp = readableEpisodes[currentIndexInList + 1];

                    if (prevEp) {
                        hasPrev = true;
                        // [CRITICAL] 0-based index for URL
                        prevIndex = prevEp.index - 1;
                    }

                    if (nextEp) {
                        hasNext = true;
                        nextIndex = nextEp.index - 1;
                    }
                }

                // Rewards & Analytics
                if (window.user) {
                    import('../services/point.service.js').then(({ PointService }) => {
                        PointService.earnReadingReward(seriesId, episodeId).catch(e => { });
                    });
                }
            }

            if (epData) {
                if (!epData.imageUrls && epData.links) epData.imageUrls = epData.links;
                import('../services/analytics.service.js').then(({ AnalyticsService }) => {
                    AnalyticsService.logView(seriesId, seriesData.authorId);
                    AnalyticsService.logHourlyView(seriesId);
                });
            }
        } catch (e) {
            console.error(e);
            return document.createTextNode("Error loading episode.");
        }
    }

    if (!epData) return document.createTextNode("Episode not found.");

    // [SECURITY] Draft Guard (Current Episode)
    const isAuthor = window.user && window.user.uid === seriesData.authorId;
    const isDraft = epData.status === 'draft';

    if (isDraft && !isAuthor) {
        window.Modal.alert("작성 중인 에피소드입니다.");
        window.router.navigate(`/series/${seriesId}`);
        return document.createElement('div');
    }


    // [PHASE 2] Initial Load Payment Gate (Webtoon)
    // [STRICT v1.36.191] Default Locked Policy
    // Logic: Limit 3 -> 1,2,3 Free. 4 Paid. (Index <= Limit)

    // 1. Fetch Global Policy
    const _globalSettings = await ContentService.getGlobalSettings();
    const _freeLimit = seriesData.freeLimit !== undefined ? seriesData.freeLimit : (_globalSettings.default_free_limit || 3);
    const _safeLimit = Number(_freeLimit);

    // 2. Check Free Policy (Standard 1-based Index)
    // EpisodeAdapter guarantees 'index' property (1, 2, 3...)
    // Current Episode = epData
    let _isFreeInit = false; // Default Locked
    if (epData.index <= _safeLimit) {
        _isFreeInit = true; // Exception
    }

    if (!_isFreeInit) {
        console.log("[Webtoon] Default Locked (Init):", { index: epData.index, limit: _safeLimit });

        if (!window.user) {
            window.Modal.toast("로그인이 필요한 유료 에피소드입니다.");
            window.router.navigate('/login');
            return document.createElement('div');
        }

        const _isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, episodeId);

        if (!_isUnlocked) {
            console.log("[Webtoon] Content Locked. Triggering Modal.");
            // Pricing Strategy
            const _SURCHARGE = seriesData.isAdult ? 50 : 0;
            const _RENT_DAYS = _globalSettings.rent_days || 7;
            const _typePolicies = _globalSettings.prices || {};
            const _policy = _typePolicies[seriesData.type] || _typePolicies['webtoon'] || { rent_price: 2, own_price: 4 };

            const _baseRent = epData.price || _policy.rent_price || 2;
            const _baseOwn = _policy.own_price || (_baseRent * 2);

            const _finalRent = _baseRent + _SURCHARGE;
            const _finalOwn = _baseOwn + _SURCHARGE;

            const _blocker = document.createElement('div');
            _blocker.className = "w-full h-screen flex flex-col items-center justify-center bg-zinc-900 text-white";
            _blocker.innerHTML = `<div class="text-center p-8"><h2 class="text-2xl font-bold mb-4">🔒 유료 에피소드</h2><p>구매 후 열람할 수 있습니다.</p>` +
                `<button onclick="window.history.back()" class="mt-4 px-4 py-2 border border-zinc-700 rounded hover:bg-zinc-800 text-sm">뒤로 가기</button>` +
                `</div>`;

            new PurchaseModal({
                title: "잠금 해제",
                itemName: `${seriesData.title} - ${epData.title}`,
                rentPrice: _finalRent,
                rentDays: _RENT_DAYS,
                ownPrice: _finalOwn,
                isAdult: seriesData.isAdult,
                onConfirm: async (type) => {
                    try {
                        const price = type === 'rent' ? _finalRent : _finalOwn;
                        await PointService.unlockContent(seriesId, episodeId, type, price, `${seriesData.title} - ${epData.title}`);
                        window.Modal.toast("결제가 완료되었습니다.");
                        window.location.reload();
                    } catch (e) { console.error(e); }
                },
                onCancel: () => { window.router.navigate(`/series/${seriesId}`); }
            }).render();
            return _blocker;
        }
    }

    // [SEO & AI Metadata Injection]
    // Fetch AI Analysis (SEO/GEO) to enrich the page metadata
    try {
        console.log(`[Webtoon] Fetching SEO Metadata for ${seriesId}/${episodeId}`);
        const aiMetaRef = doc(db, "series", seriesId, "episodes", episodeId, "ai_metadata", "seo_analysis");
        const aiMetaSnap = await getDoc(aiMetaRef);
        const aiData = aiMetaSnap.exists() ? aiMetaSnap.data() : null;

        // Dynamic Imports for SEO
        const { SEOManager } = await import('../utils/seo-manager.js');
        const { JsonLdInjector } = await import('../components/feature/seo/JsonLdInjector.js');

        // 1. Basic Meta Tags
        SEOManager.updateMeta({
            title: `${seriesData?.title || 'Webtoon'} - ${epData.title}`,
            description: aiData?.hook || epData.note || `${seriesData?.title}의 ${epData.title} 에피소드입니다.`,
            image: seriesData?.thumbnail || epData.imageUrls?.[0] || '/og.webp',
            keywords: aiData?.keywords ? aiData.keywords.join(',') : 'webtoon,comics,story'
        });

        // 2. Advanced JSON-LD (Schema.org) with GEO Data
        // Convert 'Real' locations to identifiable content
        const locationContext = aiData?.locations?.real ? aiData.locations.real : [];

        JsonLdInjector.inject({
            mode: 'comic', // or 'chapter'
            title: epData.title,
            description: aiData?.hook || epData.note || "Read this episode on Ssulwha.",
            author: seriesData?.authorId || "Unknown",
            datePublished: epData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            image: seriesData?.thumbnail || epData.imageUrls?.[0],
            // [AI GEO Injection]
            ocrContext: {
                locations: locationContext,
                vibes: aiData?.keywords || []
            }
        });

        console.log("[Webtoon] SEO Injected:", { hasAI: !!aiData, locations: locationContext });

    } catch (e) {
        console.warn("[Webtoon] SEO Injection Failed (Non-critical):", e);
    }

    const images = epData.imageUrls || [];

    // [PHASE 2 v2.1] Deterministic Display Mode
    // Removed brittle `checkRatio` logic. Now relies on explicit metadata.
    // [PHASE 2 v2.1] Deterministic Display Mode (Prioritize Episode > Series)
    const displayMode = epData.displayMode || seriesData.displayMode || 'webtoon';
    // [FIX] 'insta' is also a horizontal mode (Paged)
    const isComicMode = displayMode.startsWith('comic') || displayMode === 'insta';

    // 4. Assemble UI
    const container = ViewerLayout({ isEmbedded });
    const { main, viewerHost } = ViewerContent(isComicMode);
    container.appendChild(main);

    // 5. Initialize Modules (Navigation)
    const navCallbacks = {
        onNext: async () => {
            if (hasNext && nextIndex !== -1) {
                // [PHASE 1] Secure Payment Gate
                const targetEp = allEpisodes[nextIndex] || allEpisodes.find(e => e.index === nextIndex + 1); // Robust lookup

                if (!targetEp) {
                    window.Modal.toast("에피소드 정보를 찾을 수 없습니다.");
                    return;
                }

                // 1. Fetch Global Policy & Determine Free Limit (Robust)
                const globalSettings = await ContentService.getGlobalSettings();

                // [FIX v1.36.192] Robust Free Limit (Null & SnakeCase Guard)
                // If DB is null or undefined, use Global Default (3).
                const rawLimit = seriesData.freeLimit ?? seriesData.free_limit ?? globalSettings.default_free_limit ?? 3;
                const freeLimit = Number(rawLimit);
                const safeFreeLimit = isNaN(freeLimit) ? 3 : freeLimit;

                // 2. Check Free Policy
                // [STRICT v1.36.192] Default Locked Policy
                // Formula: isFree = (Index <= Limit)
                // CORE FIX: Force Number Comparison to prevent "1" (string) vs 3 (number) failure

                let isFree = false; // Default Locked
                const targetEpIndex = Number(targetEp.index); // Explicit Cast

                if (!isNaN(targetEpIndex) && targetEpIndex <= safeFreeLimit) {
                    isFree = true;
                }

                console.log("[Webtoon] Gate Check (Strict):", { index: targetEpIndex, limit: safeFreeLimit, isFree });

                if (isFree) {
                    // Use router to navigate using original ID or Index
                    window.router.navigate(`/series/${seriesId}/episode/${targetEp.originalId || targetEp.id || nextIndex}`);
                    return;
                }

                // 3. User & Auth Check
                if (!window.user) {
                    if (confirm("로그인이 필요합니다. 로그인하시겠습니까?")) window.router.navigate('/login');
                    return;
                }

                // 4. Check Ownership
                const isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, targetEp.id);
                if (isUnlocked) {
                    window.router.navigate(`/series/${seriesId}/episode/${nextIndex}`);
                    return;
                }

                // 5. Dynamic Pricing & Adult Surcharge
                const SURCHARGE = seriesData.isAdult ? 50 : 0;
                const RENT_DAYS = globalSettings.rent_days || 7; // [RENTAL] Dynamic Days

                // Policy Fallback for Webtoon
                const typePolicies = globalSettings.prices || {};
                const policy = typePolicies[seriesData.type] || typePolicies['webtoon'] || { rent_price: 200, own_price: 500 };

                // Calculate Final Prices
                // [FIX v1.36.192] Type Safety & Zero Guard
                let baseRent = Number(targetEp.price || policy.rent_price || 200);
                let baseOwn = Number(policy.own_price || (baseRent * 2));

                if (baseRent <= 0 || isNaN(baseRent)) {
                    console.warn("[Payment] Invalid/Zero Rent Price Detected. Forcing Default 200P.", baseRent);
                    baseRent = 200;
                }
                if (baseOwn <= 0 || isNaN(baseOwn)) {
                    baseOwn = 500;
                }

                const finalRent = baseRent + SURCHARGE;
                const finalOwn = baseOwn + SURCHARGE;

                // 6. Show Gatekeeper Modal
                new PurchaseModal({
                    title: "다음 화 잠금 해제",
                    itemName: `${seriesData.title} - ${targetEp.title}`,
                    rentPrice: finalRent,
                    rentDays: RENT_DAYS, // [RENTAL] Pass dynamic days
                    ownPrice: finalOwn,
                    isAdult: seriesData.isAdult,
                    onConfirm: async (type) => {
                        try {
                            const price = type === 'rent' ? finalRent : finalOwn;
                            // [SECURE] Unlock via Server Function
                            await PointService.unlockContent(seriesId, targetEp.id, type, price, `${seriesData.title} - ${targetEp.title}`);

                            window.Modal.toast("결제가 완료되었습니다.");
                            window.router.navigate(`/series/${seriesId}/episode/${nextIndex}`);
                        } catch (e) {
                            console.error(e);
                            throw e;
                        }
                    }
                }).render();

            } else {
                window.Modal.toast("마지막 에피소드입니다.");
                import('../services/content.service.js').then(({ ContentService }) => {
                    ContentService.markSeriesCompleted(seriesId, window.user?.uid);
                });
            }
        },
        onPrev: () => {
            if (hasPrev && prevIndex !== -1) window.router.navigate(`/series/${seriesId}/episode/${prevIndex}`);
        }
    };

    let horizontalController = null;
    let header = null;

    if (!isEmbedded) {
        // A. Header (Webtoon Mode = No Settings)
        header = new ViewerHeader(document.body, {
            title: epData.title,
            viewerType: displayMode, // [FIX] Pass exact mode (e.g. 'comic_2_3') for Header Logic
            onBack: () => window.router.navigate(`/series/${seriesId}`),
            onSearch: () => import('../components/search-modal.js').then(({ SearchModal }) => SearchModal.show())
        });

        // Smart Scroll Target for Horizontal Mode
        setTimeout(() => {
            if (isComicMode) {
                const comicContainer = container.querySelector('.comic-mode-container');
                if (comicContainer) {
                    header.setScrollTarget(comicContainer);
                    // Also init Resume Engine on this target
                    ViewerResumeEngine.init(comicContainer, {
                        seriesId, episodeId, title: epData.title, thumbnail: seriesData?.thumbnail, seriesTitle: seriesData?.title
                    }, { episodeIndex: epIndex });
                }
            } else {
                // Vertical (Window Scroll)
                ViewerResumeEngine.init(window, {
                    seriesId, episodeId, title: epData.title, thumbnail: seriesData?.thumbnail, seriesTitle: seriesData?.title
                }, { episodeIndex: epIndex });
            }
        }, 100);

        // B. Viewer Implementation
        if (isComicMode) {
            horizontalController = HorizontalViewer(viewerHost, {
                images,
                isEmbedded,
                ...navCallbacks,
                onToggleUI: () => header.toggle(),
                onHideUI: () => header.hide()
            });
        } else {
            VerticalViewer(viewerHost, { images, ...navCallbacks, hasNext, hasPrev });
        }

        // C. Footer (Attached at bottom of Main)
        const footerHost = document.createElement('div');
        footerHost.id = 'viewer-footer-host';
        main.appendChild(footerHost);

        const footer = new ViewerFooter(footerHost, {
            seriesId,
            episodeId,
            seriesData: { ...seriesData, ...options },
            hasPrev,
            hasNext,
            onPrev: navCallbacks.onPrev,
            onNext: navCallbacks.onNext
        });

        // D. Rating (Into Footer Slot)
        const ratingSlot = footerHost.querySelector('#vf-rating-slot');
        if (ratingSlot) {
            import('../components/feature/viewer/RatingComponent.js').then(({ RatingComponent }) => {
                RatingComponent(ratingSlot, {
                    seriesId,
                    episodeId,
                    initialRating: seriesData?.stats?.ratingAverage || 0, // Fallback if no episode stats
                    initialCount: seriesData?.stats?.ratingCount || 0
                });
            });
        }

        // E. Comments (Into Footer Slot)
        const commentSlot = footerHost.querySelector('#vf-comment-slot');
        if (commentSlot) {
            const searchParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
            const targetCommentId = searchParams.get('commentId');

            CommentSection(commentSlot, {
                seriesId,
                episodeId,
                currentUser: window.user,
                authorId: seriesData?.authorId,
                hasNext,
                onNext: navCallbacks.onNext,
                targetCommentId
            });
        }

        // E. FAB Controller
        // [FIX] Robust BGM Extraction for Legacy/New Compatibility
        const bgmSource = epData.bgm || epData.bgmUrl || epData.metadata?.bgm || null;

        // [New] Real-time Translation
        const { UniversalViewer } = await import('../components/feature/viewer/UniversalViewer.js');

        const controller = new ViewerController(document.body, {
            viewerType: displayMode, // [FIX] Pass exact mode for AutoPlay Timing
            bgmUrl: bgmSource,
            onScrollTop: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
            onNextPage: () => {
                if (horizontalController && horizontalController.nextPage) {
                    horizontalController.nextPage();
                }
            },
            // [FAB Integration] 9-Language Support & Universal Overlay Trigger
            onTranslate: async (isTranslated) => {
                if (!isTranslated) {
                    // [OFF] Hide Overlay
                    UniversalViewer.hide();
                    window.Modal.toast("원본 모드로 복귀합니다.");
                    return;
                }

                // 1. Reuse Existing UI (9 Languages)
                const lang = await window.Modal.showLanguageSelector("en");
                if (!lang) {
                    // Cancelled
                    controller.toggleTranslate(controller.wrapper.querySelector('#btn-translate'));
                    return;
                }

                // 2. Trigger New Engine
                // window.Modal.toast("AI가 장면과 텍스트를 분석 중입니다..."); // UniversalViewer handles this

                // [FIX] Direct Access Protocol (v1.36.272)
                // Webtoon Data is stored at 'ep_0' (Index), not UUID.
                // We strictly pass the 0-based index as the primary ID.
                const directIndex = (epData && epData.index) ? epData.index - 1 : 0;

                await UniversalViewer.show({
                    seriesId,
                    episodeId: epData.id, // [FIX] Use UUID (or Legacy Index) based on Adapter logic
                    lang: lang,
                    mode: isComicMode ? 'comic' : 'webtoon'
                });
            }
        });

        // F. Security & Continuity
        await ViewerResumeEngine.restore(isComicMode ? container.querySelector('.comic-mode-container') || window : window, seriesId, episodeId);

        // F. Security & Continuity
        await ViewerResumeEngine.restore(isComicMode ? container.querySelector('.comic-mode-container') || window : window, seriesId, episodeId);

        // [FACTORY PATTERN] Unified Fetch (Webtoon)
        import('../services/admin-popup.service.js').then(async ({ AdminPopupService }) => {
            try {
                // [Refactor] Use Centralized Resolution (Auto-Language Support)
                const finalConfig = await AdminPopupService.getResolvedCopyrightSettings();

                // [FIX] Webtoon Default Handling
                if (finalConfig.mode !== 'custom') {
                    // Default for Webtoon (Standard)
                    // No specific override needed, fallback to CopyrightWarning defaults
                } else {
                    finalConfig.icon = finalConfig.theme === 'police' ? '👮‍♂️' : (finalConfig.theme === 'horror' ? '👻' : '⚠️');
                }

                CopyrightWarning(container, finalConfig);

            } catch (e) {
                console.warn("Copyright Settings Fetch Error (Webtoon)", e);
                CopyrightWarning(container);
            }
        });

        // Cleanup
        container.destroy = () => {
            header.destroy();
            controller.destroy();
            ViewerResumeEngine.destroy(isComicMode ? container.querySelector('.comic-mode-container') || window : window);
        };
    }

    return container;
};
